'use client'

import React from 'react'

// everything is optional so you don't have to change everything at once
type NavState = {
  label: React.ReactElement | null
  CTA: React.ReactElement | null
  fixed: boolean
  isSearchVisible: boolean
  hovered: boolean
  scrollParent: React.RefObject<HTMLElement> | null
  hidden: boolean
  border: boolean
}

const DEFAULT_NAV_STATE: NavState = {
  label: null,
  CTA: null,
  fixed: false,
  isSearchVisible: false,
  hovered: false,
  scrollParent: null,
  hidden: false,
  border: false
}

export interface NavContextValues {
  navState: NavState
  changeNavState: (newState: Partial<NavState>) => void
  resetNavState: (keys?: (keyof NavState)[]) => void
}

const NavContext = React.createContext<NavContextValues | undefined>(undefined)

const NavProvider = ({ children }: { children: React.ReactNode | React.ReactNode[] }): React.ReactElement => {
  const [navState, setNavState] = React.useState<NavState>(DEFAULT_NAV_STATE)

  const resetNavState = React.useCallback(
    (keys?: (keyof NavState)[]) => {
      if (!keys) {
        setNavState(DEFAULT_NAV_STATE)
        return
      }

      setNavState((prevState) => {
        const newState: NavState = { ...prevState }
        keys.forEach((key) => {
          // We need this funky cast because TS doesn't know
          // which key we're setting and not all keys have the
          // same type value.
          ;(newState[key] as NavState[keyof NavState]) = DEFAULT_NAV_STATE[key]
        })
        return newState
      })
    },
    [setNavState]
  )

  const changeNavState = React.useCallback(
    (newState: Partial<NavState>) => {
      setNavState((prevState) => ({
        ...prevState,
        ...newState
      }))
    },
    [setNavState]
  )

  return (
    <NavContext.Provider
      value={{
        navState,
        changeNavState,
        resetNavState
      }}
    >
      {children}
    </NavContext.Provider>
  )
}

function useNavBar(navConfig?: Partial<NavState>): NavState {
  const context = React.useContext(NavContext)
  if (context === undefined) {
    throw new Error('useNavBar must be used within a NavProvider')
  }

  const { changeNavState, resetNavState } = context

  const readOnly = !navConfig
  navConfig = navConfig ?? {}

  // memoize config so that consumer components don't need to
  // memoize themselves.
  const config = React.useMemo(
    () => ({
      CTA: navConfig?.CTA,
      fixed: navConfig?.fixed,
      hovered: navConfig?.hovered,
      scrollParent: navConfig?.scrollParent,
      hidden: navConfig?.hidden,
      isSearchVisible: navConfig?.isSearchVisible,
      label: navConfig?.label,
      border: navConfig?.border
    }),
    [
      navConfig?.CTA,
      navConfig?.fixed,
      navConfig?.hovered,
      navConfig?.isSearchVisible,
      navConfig?.label,
      navConfig?.scrollParent,
      navConfig?.hidden,
      navConfig?.border
    ]
  )

  const navConfigKeys = Object.keys(navConfig).join(',')
  React.useEffect(() => {
    if (readOnly) {
      return
    }

    // Filter out undefined values to avoid resetting them
    changeNavState(Object.fromEntries(Object.entries(config).filter(([_, value]) => value !== undefined)))

    return () => {
      const keys = navConfigKeys.split(',')
      resetNavState(keys as (keyof NavState)[])
    }
  }, [changeNavState, config, resetNavState, readOnly, navConfigKeys])

  return context.navState
}

export { NavProvider, useNavBar }
